import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { authorizeUser } from 'features/room/thunks/authorizeUser';
import { PermissionsState, PermissionTypes, RoleEntries } from 'features/permissions/types';
import { RootState } from 'store/store';
import { normalizePermissions } from 'features/permissions/utils/normalizePermissions';
import { selectLocalUser, selectLocalUserRole } from 'features/users/usersSlice';
import { HasPermissionsOptions } from 'features/permissions/utils/checkPermissions';
import { refinePermissions } from 'features/permissions/utils/refinePermissions';
import { selectFeatureFlag } from 'features/feature-set/featuresSlice';
import { selectIsSystemSession } from 'features/application/selectors';
import { selectScreenshareStreams } from 'features/layout/features/order/orderSlice';

export const initialState: PermissionsState = {
  byRole: {},
  allRoles: [],
  targetRolesMap: {},
  rolePermissions: {},
  coverageAcrossRoles: {},
  defaultRole: '',
};

export const permissionsSlice = createSlice({
  name: 'permissions',
  initialState,
  reducers: {
    rolesLocalized(state, action: PayloadAction<RoleEntries>) {
      state.byRole = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(authorizeUser.fulfilled, (state, action) => {
      const { roles, settings } = action.payload;

      const { byRole, rolePermissions, allRoles, targetRolesMap, coverageAcrossRoles } =
        normalizePermissions(roles);

      state.coverageAcrossRoles = coverageAcrossRoles;
      state.rolePermissions = rolePermissions;
      state.byRole = byRole;
      state.allRoles = allRoles;
      state.targetRolesMap = targetRolesMap;
      state.defaultRole = settings.defaultRole;
    });
  },
});

export const { rolesLocalized } = permissionsSlice.actions;

export const selectDefaultRole = (state: RootState) => state.permissions.defaultRole;

export const selectPermissionsState = (state: RootState) => state.permissions;

export const selectRoleEntries = (state: RootState) => state.permissions.byRole;
export const selectAllRoleNames = (state: RootState) => state.permissions.allRoles;

export const selectTargetRolesMap = (state: RootState) => state.permissions.targetRolesMap;

export const selectRolePermissions = (state: RootState) => state.permissions.rolePermissions;

export const selectLocalBroadcastPermissions = createSelector(
  [selectPermissionsState, selectLocalUser],
  (permissionsState, localUser) => {
    const canBroadcastVideo = refinePermissions(PermissionTypes.broadcastVideo, {
      permissionsState,
      localUser,
    });

    const canBroadcastAudio = refinePermissions(PermissionTypes.broadcastAudio, {
      permissionsState,
      localUser,
    });

    return canBroadcastVideo || canBroadcastAudio;
  }
);

export const selectHasPermissions = createSelector(
  [
    selectPermissionsState,
    selectLocalUser,
    (state, permissions: PermissionTypes | PermissionTypes[]) => permissions,
    (state, permissions: PermissionTypes | PermissionTypes[], options?: HasPermissionsOptions) =>
      options,
  ],
  (permissionsState, localUser, permissions, options) =>
    refinePermissions(permissions, {
      permissionsState,
      localUser,
      ...(options || {}),
    })
);

export const selectInviteAvailable = createSelector(
  [
    (state) => selectFeatureFlag(state, 'invite'),
    (state) => selectHasPermissions(state, PermissionTypes.inviteParticipant),
  ],
  (inviteEnabled, canInviteParticipant) => inviteEnabled && canInviteParticipant
);

export const selectCanManagePrivateWhiteboards = createSelector(
  [
    selectIsSystemSession,
    selectPermissionsState,
    selectLocalUser,
    (state, options?: HasPermissionsOptions) => options,
  ],
  (isSystemSession, permissionsState, localUser, options) => {
    if (isSystemSession) {
      return false;
    }

    return refinePermissions(PermissionTypes.managePrivateWhiteboards, {
      permissionsState,
      localUser,
      ...(options || {}),
    });
  }
);

export const selectCanManageAllScreenshares = createSelector(
  [selectScreenshareStreams, selectLocalUser, selectPermissionsState, selectIsSystemSession],
  (screenshares, localUser, permissionsState, isSystemSession) =>
    screenshares.every((stream) =>
      refinePermissions(PermissionTypes.manageScreenshare, {
        permissionsState,
        localUser,
        isSystemSession,
        targetRole: stream.user?.role,
      })
    )
);

export const selectRolesToManage = createSelector(
  [selectLocalUserRole, selectTargetRolesMap, selectRoleEntries],
  (localRole, targetRolesMap, entries) => {
    const rolesToManage = targetRolesMap[localRole][PermissionTypes.manageRoles];

    if (!rolesToManage) {
      return null;
    }

    return rolesToManage.map((role) => ({
      value: role,
      displayName: entries[role].displayName,
    }));
  }
);

export const selectRoles = createSelector(
  [selectAllRoleNames, selectRoleEntries],
  (roleNames, roleEntries) =>
    roleNames.map((role) => ({
      name: role,
      displayName: roleEntries[role].displayName,
    }))
);

export const selectRoleDisplayName = createSelector(
  [selectRoleEntries, (state, role) => role],
  (entries, role) => entries[role]?.displayName
);

export const selectRolesWithPrivateWhiteboardPermission = createSelector(
  [selectRolePermissions, selectRoleEntries],
  (rolePermissions, roleEntries) => {
    const rolesWithPermission: string[] = [];

    for (const [roleName, permissions] of Object.entries(rolePermissions)) {
      if (permissions[PermissionTypes.managePrivateWhiteboards] === true) {
        rolesWithPermission.push(roleEntries[roleName].displayName);
      }
    }

    return rolesWithPermission;
  }
);

export default permissionsSlice.reducer;
